// v0.5.0c+2 — Country-rate token filler for `<ReasonPicker>`.
//
// Why this exists: audit-reason preset templates contain tokens
// like `<RATE_DESC>` / `<RATE>` that the operator must replace
// before saving (placeholder-fail-loud round C). Letting the
// operator TYPE the rate ("16%", "16.0%", "16 percent", "Std.
// 16%" …) invites typos and inconsistency. Each country has only
// a handful of valid rates — show them in a dropdown and
// substitute on pick.
//
// Public surface (rendered INSIDE `<ReasonPicker>` between the
// preset dropdown and the textarea — opt-in via the `country` +
// `onDate` props on the picker):
//
//   <RateTokenFiller
//     country="DE"
//     onDate="2025-04-15"
//     regime="EU_OSS"             // optional, defaults EU_OSS
//     value={text}                // current textarea content
//     onChange={(newText) => …}   // first-occurrence substitution
//     testid={`${parentTestid}-rate-filler`}
//   />
//
// Tokens handled: `<RATE_DESC>` (substitutes the label, e.g.
// "19% standard") and `<RATE>` (substitutes the bare percentage,
// e.g. "19" — matches the existing template wording "<RATE>%").
//
// Fail-loud: if the rates fetch errors, the filler shows the
// error inline. The operator can still type manually, but they
// can't claim "the UI didn't tell me which rates exist".
import React, { useEffect, useMemo, useState } from "react";
import { getCountryRates } from "../lib/api";
import { errorMessage } from "../lib/errorMessage";
import { useTranslation } from "react-i18next";

// Module-level cache — `country|onDate|regime` → rates payload.
// Avoids refetching when the picker re-renders on every keystroke.
const _CACHE = new Map();

const cacheKey = (country, onDate, regime) =>
  `${country}|${onDate}|${regime || "EU_OSS"}`;

const fetchRates = async (country, onDate, regime) => {
  const k = cacheKey(country, onDate, regime);
  if (_CACHE.has(k)) return _CACHE.get(k);
  const data = await getCountryRates(country, onDate, regime);
  _CACHE.set(k, data);
  return data;
};

// Tokens we know how to fill from a country rate table.
// Keep the regex narrow — anything else stays free-text.
const TOKEN_SPEC = {
  "<RATE_DESC>": "label",   // "19% standard"
  "<RATE>": "rate_pct",     // "19"
};

const substituteFirst = (text, token, replacement) => {
  const idx = text.indexOf(token);
  if (idx < 0) return text;
  return text.slice(0, idx) + replacement + text.slice(idx + token.length);
};

export const RateTokenFiller = ({
  country, onDate, regime, value, onChange, testid,
}) => {
  const { t } = useTranslation();
  const [rates, setRates] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!country || !onDate) return;
    let cancelled = false;
    setLoading(true); setErr(null);
    fetchRates(country, onDate, regime)
      .then((d) => { if (!cancelled) setRates(d.rates || []); })
      .catch((e) => { if (!cancelled) setErr(errorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [country, onDate, regime]);

  // Detect which known tokens are still present in `value`. We
  // re-detect on every render because the textarea changes as
  // the operator types — the filler dropdowns appear/disappear
  // in lockstep with what's left to fill.
  const presentTokens = useMemo(() => {
    if (!value) return [];
    return Object.keys(TOKEN_SPEC).filter((tok) => value.includes(tok));
  }, [value]);

  if (!country || !onDate) return null;
  if (presentTokens.length === 0) return null;

  if (loading) {
    return (
      <div
        data-testid={`${testid}-loading`}
        className="text-[10px] text-slate-500 dark:text-slate-400"
      >
        {t("ui.rate_filler.loading", { country, defaultValue: `Loading ${country} rates…` })}
      </div>
    );
  }

  if (err) {
    return (
      <div
        data-testid={`${testid}-error`}
        className="text-[10px] text-rose-700 dark:text-rose-300"
      >
        {t("ui.rate_filler.load_failed", { country, err, defaultValue: `Could not load ${country} rate table: ${err}. Type the rate by hand.` })}
      </div>
    );
  }

  if (!rates || rates.length === 0) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className="text-[10px] text-slate-500 dark:text-slate-400"
      >
        {t("ui.rate_filler.empty", { country, defaultValue: `No rates listed for ${country}.` })}
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      className="space-y-1 border-l-2 border-amber-400 dark:border-amber-600 pl-2"
    >
      <div className="text-[10px] text-amber-700 dark:text-amber-300">
        {rates.length === 1
          ? t("ui.rate_filler.heading_single", { country, count: rates.length, defaultValue: `${country} rate table (${rates.length} rate) — pick to fill the placeholder` })
          : t("ui.rate_filler.heading_plural", { country, count: rates.length, defaultValue: `${country} rate table (${rates.length} rates) — pick to fill the placeholder` })}
      </div>
      {presentTokens.map((tok) => {
        const field = TOKEN_SPEC[tok];
        return (
          <div key={tok} className="flex items-center gap-2">
            <span className="tabular-nums text-[10px] font-bold text-amber-700 dark:text-amber-200 w-24">
              {tok}:
            </span>
            <select
              data-testid={`${testid}-select-${tok.replace(/[<>]/g, "").toLowerCase()}`}
              defaultValue=""
              onChange={(e) => {
                const rate = rates.find((r) => r.key === e.target.value);
                if (!rate) return;
                onChange(substituteFirst(value, tok, rate[field]));
                // reset so picking the same rate twice still fires.
                e.target.value = "";
              }}
              className="flex-1 bg-white dark:bg-slate-900 border border-amber-400/60 dark:border-amber-600/60 rounded px-2 py-0.5 text-[11px] text-slate-900 dark:text-slate-50 cursor-pointer"
            >
              <option value="" disabled>
                {t("ui.rate_filler.pick", { country, defaultValue: `Pick a ${country} rate…` })}
              </option>
              {rates.map((r) => (
                <option
                  key={r.key}
                  value={r.key}
                  data-testid={`${testid}-rate-${r.key}`}
                >
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        );
      })}
    </div>
  );
};

// Test-only — clear the in-module cache between scenarios.
export const _resetRateFillerCache = () => _CACHE.clear();

export default RateTokenFiller;
